import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '@elementary/common-ui/components';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { WarehouseType } from '@elementary/common-ui/types';
import { CircularProgress, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { TEST_TEMPLATES } from '@elementary/common-ui/constants/queryKeys';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import * as api from 'testManagement/services/api';
import { elementaryTableTests, elementaryColumnTests } from './elementaryTests';
import QualityDimensionTabs, {
  TestCatalogFilters,
} from './QualityDimensionTabs';
import TestCardsSection, { CardTemplate } from './TestCardsSection';

const Wrapper = styled(Flex)`
  height: 100%;
  width: 100%;
`;

type Props = {
  watch: UseFormWatch<AddTestForm>;
  setValue: UseFormSetValue<AddTestForm>;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  warehouseType?: WarehouseType;
};

const WizardTestStage = ({
  watch,
  setValue,
  tableResourcesData,
  columnsResourcesData,
  warehouseType,
}: Props) => {
  const [testCatalogFilters, setTestCatalogFilters] =
    useState<TestCatalogFilters>({ quickFilter: 'all' });

  const resourceType = watch('resourceType');
  const selectedResourcesIds = watch('resources');

  const selectedEnvId = useSelectedEnv();

  const { shouldConnectCodeRepo } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });

  const { data: testTemplates, isLoading } = useQuery(
    [TEST_TEMPLATES, resourceType],
    () =>
      api.getTestTemplates(
        resourceType === 'tables' ? 'table' : 'column',
        selectedEnvId!,
      ),
  );

  const selectedTableResourcesData = useMemo(() => {
    if (!selectedResourcesIds?.length || resourceType !== 'tables') return [];

    return tableResourcesData.filter((resource) =>
      selectedResourcesIds.includes(resource.id),
    );
  }, [tableResourcesData, resourceType, selectedResourcesIds]);

  const selectedColumnResourcesData = useMemo(() => {
    if (!selectedResourcesIds?.length || resourceType !== 'columns') return [];

    return columnsResourcesData.filter((resource) =>
      selectedResourcesIds.includes(resource.id),
    );
  }, [columnsResourcesData, resourceType, selectedResourcesIds]);

  const elementaryTests = useMemo(() => {
    const tests = [
      ...(resourceType === 'tables'
        ? elementaryTableTests
        : elementaryColumnTests),
    ];

    if (!shouldConnectCodeRepo) {
      if (
        (resourceType === 'tables' &&
          selectedTableResourcesData.every(
            (table) => table.source_type === 'dbt',
          )) ||
        (resourceType === 'columns' &&
          selectedColumnResourcesData.every(
            (column) => column.source_type === 'dbt',
          ))
      ) {
        return tests;
      }
    }

    return tests.filter((test) => isElementaryCloudTest(test.test_name));
  }, [
    resourceType,
    shouldConnectCodeRepo,
    selectedTableResourcesData,
    selectedColumnResourcesData,
  ]);

  const testTemplatesData = useMemo(() => {
    if (!shouldConnectCodeRepo) {
      if (
        (resourceType === 'columns' &&
          selectedColumnResourcesData.every(
            (column) => column.source_type === 'dbt',
          )) ||
        (resourceType === 'tables' &&
          selectedTableResourcesData.every(
            (table) => table.source_type === 'dbt',
          ))
      ) {
        return testTemplates?.test_templates;
      }
    }
    const filteredTestTemplates = testTemplates?.test_templates.filter((test) =>
      isElementaryCloudTest(test.test_name),
    );
    return filteredTestTemplates;
  }, [
    shouldConnectCodeRepo,
    testTemplates,
    selectedColumnResourcesData,
    selectedTableResourcesData,
    resourceType,
  ]);

  const selectedTemplateId =
    watch('testType') === 'yamlTest'
      ? watch('yamlTemplateName')
      : watch('testType');

  const handleCardClick = (template: CardTemplate) => {
    const isYamlTemplate = template.testType === 'yamlTest';
    const clickedTemplateId = isYamlTemplate ? template.key : template.testType;

    const isDeselect =
      !!selectedTemplateId && clickedTemplateId === selectedTemplateId;

    if (isDeselect) {
      setValue('testType', '');
      setValue('yamlTemplateName', '');
    } else {
      setValue('testType', template.testType);
      if (isYamlTemplate) {
        setValue('yamlTemplateName', template.key);
      } else {
        setValue('yamlTemplateName', '');
      }
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Wrapper divider={<Divider orientation="vertical" light />}>
      <QualityDimensionTabs
        value={testCatalogFilters}
        onChange={setTestCatalogFilters}
        elementaryTests={elementaryTests}
        testsTemplatesData={testTemplatesData}
      />
      <TestCardsSection
        testCatalogFilters={testCatalogFilters}
        selectedTemplateId={selectedTemplateId}
        onCardClick={handleCardClick}
        elementaryTests={elementaryTests}
        testsTemplatesData={testTemplatesData}
        shouldConnectCodeRepo={shouldConnectCodeRepo}
        selectedTableResourcesData={selectedTableResourcesData}
        warehouseType={warehouseType}
      />
    </Wrapper>
  );
};

export default WizardTestStage;
